import { useCallback, useEffect, useState } from 'react';
import { api, errMsg } from '../lib/api';

/** 馆内逝者：双人合葬会有 2 位，sortOrder 0 为主逝者 */
export interface MemorialPerson {
  id: string;
  name: string;
  gender: string | null;
  birthDate: string | null;
  deathDate: string | null;
  portraitUrl: string | null;
  sortOrder: number;
}

export interface MemorialData {
  id: string;
  slug: string;
  deceasedName: string;
  deceasedNameEn: string | null;
  birthDate: string | null;
  deathDate: string | null;
  biography: string | null;
  epitaph: string | null;
  visibility: string;
  themeId: string;
  templateId: string;
  memorialType: string;
  heroCategory: string | null;
  heroEra: string | null;
  tributeCount: number;
  viewCount: number;
  /** 后端标记的编辑权限，用于前端显示编辑入口 */
  canEdit?: boolean;
  /** 头像/遗像（已签名的临时访问地址），创建时可上传 */
  portraitUrl?: string | null;
  /** 馆内逝者列表（双人合葬为 2 位）；单人馆也可能为空数组 */
  people?: MemorialPerson[];
}

export interface ProductItem {
  id: string;
  categoryId: string;
  name: string;
  blessing: string | null;
  tier: 'free' | 'paid';
  priceCents: number;
  originalPriceCents: number | null;
  fallbackEmoji: string | null;
  iconSvg: string | null;
  effectType: string;
  badgeText: string | null;
  sortOrder: number;
}

export interface Offering {
  id: string;
  dedication: string | null;
  fromName: string;
  isAnonymous: boolean;
  isPinned: boolean;
  createdAt: number;
  product: {
    id: string;
    name: string;
    tier: string;
    fallbackEmoji: string | null;
    iconSvg: string | null;
    effectType: string;
  };
}

export interface ThemeItem {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  config: Record<string, string>;
}

export interface MediaItem {
  id: string;
  type: 'photo' | 'video' | 'audio';
  caption: string | null;
  sizeBytes: number;
  url: string;
  createdAt: number;
}

/**
 * 纪念馆页数据加载。
 * 所有模板共用同一份数据，各自只负责渲染，避免重复实现加载逻辑。
 */
export function useMemorialData(slug: string | undefined) {
  const [memorial, setMemorial] = useState<MemorialData | null>(null);
  const [offerings, setOfferings] = useState<Offering[]>([]);
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [groups, setGroups] = useState<{ categoryId: string; items: ProductItem[] }[]>([]);
  const [theme, setTheme] = useState<ThemeItem | null>(null);
  const [needPassword, setNeedPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const loadMemorial = useCallback(
    async (unlockToken = '') => {
      if (!slug) return;
      setLoading(true);
      setError('');

      const headers: Record<string, string> = {};
      if (unlockToken) headers['X-Memorial-Unlock'] = unlockToken;

      const res = await api<{ memorial: MemorialData; canEdit?: boolean }>(
        `/memorials/${slug}`,
        { headers }
      );

      if (res.ok && res.data?.memorial) {
        const m = res.data.memorial;
        const people = m.people ?? [];
        setMemorial({
          ...m,
          people,
          // 双人合葬：把两位姓名合成馆名，所有模板无需各自改造即可显示合葬
          deceasedName:
            people.length > 1 ? people.map((p) => p.name).join(' · ') : m.deceasedName,
          canEdit: res.data.canEdit,
        });
        setNeedPassword(false);
      } else if (res.error?.code === 'PASSWORD_REQUIRED') {
        setNeedPassword(true);
      } else {
        setError(errMsg(res, '加载失败'));
      }
      setLoading(false);
    },
    [slug]
  );

  const loadOfferings = useCallback(async (memorialId: string) => {
    const res = await api<{ items: Offering[] }>(`/offerings/memorial/${memorialId}`);
    if (res.ok && res.data) setOfferings(res.data.items);
  }, []);

  const loadMedia = useCallback(async (memorialId: string) => {
    const res = await api<{ items: MediaItem[] }>(`/media/memorial/${memorialId}`);
    if (res.ok && res.data) setMedia(res.data.items);
  }, []);

  const loadProducts = useCallback(async () => {
    const res = await api<{ groups: { categoryId: string; items: ProductItem[] }[] }>('/products');
    if (res.ok && res.data?.groups) setGroups(res.data.groups);
  }, []);

  // 主题按纪念馆配置单独拉取
  useEffect(() => {
    if (!memorial?.themeId) return;
    api<{ theme: ThemeItem }>(`/themes/${memorial.themeId}`).then((res) => {
      if (res.ok && res.data?.theme) setTheme(res.data.theme);
    });
  }, [memorial?.themeId]);

  useEffect(() => {
    loadMemorial();
    loadProducts();
  }, [slug]);

  useEffect(() => {
    if (!memorial?.id) return;
    loadOfferings(memorial.id);
    loadMedia(memorial.id);
  }, [memorial?.id, loadOfferings, loadMedia]);

  return {
    memorial,
    offerings,
    media,
    groups,
    theme,
    needPassword,
    error,
    loading,
    loadMemorial,
    loadOfferings,
    loadMedia,
    loadProducts,
  };
}
